/**
 * Skeleton Loaders for FOS Application
 * Provides visual feedback during API calls
 */

import React from 'react';
import { Skeleton } from './ui/skeleton';
import { Card, CardContent, CardHeader } from './ui/card';

/**
 * Dashboard Stats Skeleton
 * Used on FranchiseDashboard for Today's Sales card
 */
export const DashboardStatsSkeleton = () => (
  <Card className="h-full">
    <CardHeader className="pb-2">
      <div className="flex items-center gap-2">
        <Skeleton className="h-5 w-5 rounded-full" />
        <Skeleton className="h-5 w-32" />
      </div>
    </CardHeader>
    <CardContent>
      <Skeleton className="h-8 w-24 mb-2" />
      <Skeleton className="h-4 w-20" />
    </CardContent>
  </Card>
);

/**
 * Menu Item Grid Skeleton
 * Used on GenerateOrder page while menu items load
 */
export const MenuItemsSkeleton = ({ count = 12 }) => (
  <div className="grid grid-cols-3 xs:grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 2xl:grid-cols-12 gap-1 sm:gap-1.5">
    {Array.from({ length: count }).map((_, i) => (
      <Card key={i} className="shadow-sm border-[0.5px] border-gray-200">
        <CardContent className="p-1 sm:p-1.5 min-h-[45px] sm:min-h-[50px]">
          <Skeleton className="h-3 w-full mb-1" />
          <Skeleton className="h-3 w-2/3 mb-2" />
          <Skeleton className="h-3 w-8" />
        </CardContent>
      </Card>
    ))}
  </div>
);

/**
 * Category Sidebar Skeleton
 * Used on GenerateOrder page while categories load
 */
export const CategoriesSkeleton = ({ count = 6 }) => (
  <div className="p-3 xl:p-4 space-y-2">
    {Array.from({ length: count }).map((_, i) => (
      <Skeleton key={i} className="h-10 w-full rounded-xl" />
    ))}
  </div>
);

/**
 * Ledger Party Card Skeleton
 * Used on LedgerManagement page while parties load
 */
export const LedgerPartySkeleton = ({ count = 5 }) => (
  <div className="space-y-3">
    {Array.from({ length: count }).map((_, i) => (
      <Card key={i} className="overflow-hidden">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div>
                <Skeleton className="h-4 w-32 mb-2" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
            <div className="text-right">
              <Skeleton className="h-5 w-16 mb-1" />
              <Skeleton className="h-3 w-12" />
            </div>
          </div>
        </CardContent>
      </Card>
    ))}
  </div>
);

/**
 * Order List Skeleton
 * Used on FranchiseDashboard for recent orders
 */
export const OrderListSkeleton = ({ count = 5 }) => (
  <div className="space-y-2">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
        <div className="flex items-center gap-3">
          <Skeleton className="h-8 w-8 rounded-full" />
          <div>
            <Skeleton className="h-4 w-28 mb-1" />
            <Skeleton className="h-3 w-20" />
          </div>
        </div>
        <Skeleton className="h-5 w-16" />
      </div>
    ))}
  </div>
);

/**
 * Sales Insights Chart Skeleton
 * Used on FranchiseDashboard for the sales chart area
 */
export const SalesChartSkeleton = () => (
  <Card>
    <CardHeader className="pb-2">
      <div className="flex items-center justify-between">
        <Skeleton className="h-5 w-28" />
        <div className="flex gap-2">
          <Skeleton className="h-8 w-8 rounded" />
          <Skeleton className="h-8 w-8 rounded" />
          <Skeleton className="h-8 w-20 rounded" />
        </div>
      </div>
    </CardHeader>
    <CardContent>
      <div className="h-[200px] flex items-end justify-between gap-2 pt-4">
        {Array.from({ length: 7 }).map((_, i) => (
          <Skeleton 
            key={i} 
            className="flex-1" 
            style={{ height: `${Math.random() * 60 + 40}%` }} 
          />
        ))}
      </div>
    </CardContent>
  </Card>
);

/**
 * Transaction History Skeleton
 * Used in Ledger transaction modal
 */
export const TransactionsSkeleton = ({ count = 5 }) => (
  <div className="space-y-3">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="flex items-center justify-between p-3 border border-border rounded-lg">
        <div className="flex items-center gap-3">
          <Skeleton className="h-8 w-8 rounded-full" />
          <div>
            <Skeleton className="h-4 w-24 mb-1" />
            <Skeleton className="h-3 w-32" />
          </div>
        </div>
        <div className="text-right">
          <Skeleton className="h-4 w-16 mb-1" />
          <Skeleton className="h-3 w-20" />
        </div>
      </div>
    ))}
  </div>
);

/**
 * Staff Card Skeleton
 * Used on StaffManagement page
 */
export const StaffCardSkeleton = ({ count = 4 }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
    {Array.from({ length: count }).map((_, i) => (
      <Card key={i}>
        <CardContent className="p-4">
          <div className="flex items-center gap-3 mb-3">
            <Skeleton className="h-12 w-12 rounded-full" />
            <div>
              <Skeleton className="h-4 w-28 mb-2" />
              <Skeleton className="h-3 w-20" />
            </div>
          </div>
          <div className="space-y-2">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-3/4" />
          </div>
          <div className="flex gap-2 mt-4">
            <Skeleton className="h-8 flex-1 rounded" />
            <Skeleton className="h-8 flex-1 rounded" />
          </div>
        </CardContent>
      </Card>
    ))}
  </div>
);

/**
 * Quick Actions Skeleton
 * Used on FranchiseDashboard for the quick actions card
 */
export const QuickActionsSkeleton = () => (
  <Card>
    <CardHeader className="pb-2">
      <div className="flex items-center justify-between">
        <Skeleton className="h-5 w-28" />
        <Skeleton className="h-4 w-32" />
      </div>
    </CardHeader>
    <CardContent className="space-y-3">
      <Skeleton className="h-12 w-full rounded-xl" />
      <Skeleton className="h-12 w-full rounded-xl" />
    </CardContent>
  </Card>
);

/**
 * Full Page Loading Skeleton
 * Used for initial page loads
 */
export const PageLoadingSkeleton = () => (
  <div className="min-h-screen bg-background p-4 md:p-6">
    {/* Header */}
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center gap-3">
        <Skeleton className="h-12 w-12 rounded-lg" />
        <div>
          <Skeleton className="h-6 w-40 mb-2" />
          <Skeleton className="h-4 w-28" />
        </div>
      </div>
      <div className="flex gap-2">
        <Skeleton className="h-10 w-10 rounded-full" />
        <Skeleton className="h-10 w-20 rounded-lg" />
      </div>
    </div>
    
    {/* Content Grid */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
      <DashboardStatsSkeleton />
      <QuickActionsSkeleton />
    </div>
    
    <SalesChartSkeleton />
  </div>
);

/**
 * Payment Modal Skeleton
 * Used in GenerateOrder payment modal while parties load
 */
export const PaymentModalSkeleton = () => (
  <div className="space-y-4">
    {/* Payment Methods */}
    <div>
      <Skeleton className="h-4 w-28 mb-2" />
      <div className="grid grid-cols-2 gap-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i} className="cursor-pointer">
            <CardContent className="p-3 flex items-center gap-2">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div>
                <Skeleton className="h-4 w-16 mb-1" />
                <Skeleton className="h-3 w-24" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
    
    {/* Party Ledgers */}
    <div className="pt-2 border-t border-border">
      <Skeleton className="h-4 w-28 mb-2" />
      <Skeleton className="h-9 w-full rounded-lg mb-2" />
      <div className="space-y-2 max-h-40">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div>
                  <Skeleton className="h-4 w-24 mb-1" />
                  <Skeleton className="h-3 w-20" />
                </div>
              </div>
              <Skeleton className="h-4 w-12" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  </div>
);

export default {
  DashboardStatsSkeleton,
  MenuItemsSkeleton,
  CategoriesSkeleton,
  LedgerPartySkeleton,
  OrderListSkeleton,
  SalesChartSkeleton,
  TransactionsSkeleton,
  StaffCardSkeleton,
  QuickActionsSkeleton,
  PageLoadingSkeleton,
  PaymentModalSkeleton
};
